import { UserButton, useUser } from '@clerk/clerk-react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useRef } from 'react';
import type { MouseEvent } from 'react';

import { useCurrentUser } from '@core/auth';
import { useTranslation } from '@core/i18n';
import { Tooltip } from '@core/ui';
import { cn } from '@core/utils/cn';

import { LanguageSwitcher } from './LanguageSwitcher';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { ThemeToggle } from './ThemeToggle';

interface SideNavFooterProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Closes the mobile navigation sheet after an organization switch. */
  onNavigate?: () => void;
}

/**
 * Language, theme, identity and the collapse control, pinned to the bottom of the rail.
 *
 * <p>These belong together because they are all "about me and this session" rather than
 * about the fleet -- and putting them here keeps the top bar free for what the current
 * page is actually doing.
 *
 * <p>The live-connection pill used to sit here too and has moved onto the fleet map. It
 * is a statement about the data on screen, not about the session, and four separated
 * rows made the foot of the rail heavier than the navigation above it.
 */
export function SideNavFooter({ collapsed, onToggleCollapsed, onNavigate }: SideNavFooterProps) {
  const me = useCurrentUser();
  const { user } = useUser();
  const { t } = useTranslation();
  const toggleLabel = collapsed ? t.nav.expand : t.nav.collapse;
  const identityRef = useRef<HTMLDivElement>(null);

  /**
   * The whole identity row opens Clerk's account menu, not just the 28px avatar. Clerk
   * exposes no "open" API for its UserButton, so the row forwards the click to the
   * avatar's own trigger -- unless the click already landed on it, which would toggle the
   * menu open and straight shut again.
   */
  const openAccountMenu = (event: MouseEvent<HTMLDivElement>) => {
    const trigger = identityRef.current?.querySelector<HTMLElement>('.cl-userButtonTrigger');
    if (!trigger || trigger.contains(event.target as Node)) return;
    trigger.click();
  };

  return (
    <div className="border-separator mt-auto border-t">
      {/* Renders nothing for someone with one organization and no platform role. */}
      <div className="border-separator border-b px-2 py-2 empty:hidden">
        <OrganizationSwitcher collapsed={collapsed} onNavigate={onNavigate} />
      </div>

      {/* One row, the two controls pushed to opposite ends so they read as a pair of
          settings rather than as two things that happen to be adjacent. */}
      <div
        className={cn(
          'flex items-center gap-2 px-2 py-2',
          collapsed ? 'flex-col justify-center gap-1.5' : 'justify-between',
        )}
      >
        <LanguageSwitcher collapsed={collapsed} />
        <ThemeToggle collapsed={collapsed} />
      </div>

      {/* Mouse convenience only: keyboard users reach the same menu through the avatar,
          which is Clerk's own focusable button, so the row is not a second tab stop. */}
      <div
        ref={identityRef}
        onClick={openAccountMenu}
        className={cn(
          'border-separator flex cursor-pointer items-center gap-2.5 border-t px-3 py-2.5',
          'hover:bg-fill-subtle transition-colors duration-150',
          collapsed && 'justify-center px-0',
        )}
      >
        <UserButton afterSignOutUrl="/sign-in" />
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[13px] font-medium">{me.name || user?.id}</p>
            <p className="text-fg-subtle truncate text-[11px]">
              {me.role ? t.role[me.role] : ''}
              {me.organizationName ? ` · ${me.organizationName}` : ''}
            </p>
          </div>
        )}
      </div>

      <div className="border-separator border-t p-2">
        <Tooltip content={toggleLabel} side="right">
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            className={cn(
              'text-fg-muted flex h-9 w-full items-center gap-3 rounded-md px-3 text-[13px]',
              'hover:bg-fill-subtle hover:text-fg transition-colors duration-150',
              'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
              collapsed && 'justify-center px-0',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4 shrink-0" />
            ) : (
              <PanelLeftClose className="size-4 shrink-0" />
            )}
            {!collapsed && <span>{toggleLabel}</span>}
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
